import { ChangeDetectionStrategy, Component, computed, effect, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { MaskitoDirective } from '@maskito/angular';
import { maskitoDateOptionsGenerator } from '@maskito/kit';
import { CategoryOption, NotificationDialogPayload } from '../../models/notification-dialog.models';
import {
  dateRangeValidator,
  extractRichTextPlainText,
  richTextRequiredValidator,
} from '../../utils/notification-dialog.validators';
import { SelectComponent } from '../select/select';
import { SelectOption } from '../../models/select.models';

@Component({
  selector: 'app-notification-dialog',
  imports: [ReactiveFormsModule, QuillModule, MaskitoDirective, SelectComponent],
  templateUrl: './notification-dialog.html',
  styleUrl: './notification-dialog.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationDialog {
  readonly isOpen = input(false);
  readonly isLoading = input(false);
  readonly title = input('Notifica generale');
  readonly description = input('');
  readonly descriptionItems = input<string[]>([]);
  readonly submitLabel = input('Invia notifica');
  readonly cancelLabel = input('Annulla');
  readonly showDateRange = input(false);
  readonly categories = input<CategoryOption[]>([]);

  readonly showCategories = computed(() => this.categories().length > 0);
  readonly categoryOptions = computed<SelectOption<number>[]>(() =>
    this.categories().map((cat) => ({ label: cat.name, value: cat.id })),
  );

  readonly submitForm = output<NotificationDialogPayload>();
  readonly closeDialog = output<void>();

  readonly dateMask = maskitoDateOptionsGenerator({ mode: 'dd/mm/yyyy', separator: '/' });

  readonly notificationForm = new FormGroup(
    {
      subject: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      message: new FormControl('', {
        nonNullable: true,
        validators: [richTextRequiredValidator],
      }),
      validFrom: new FormControl('', { nonNullable: true }),
      validTo: new FormControl('', { nonNullable: true }),
      category: new FormControl<number | null>(null),
    },
    { validators: [dateRangeValidator] },
  );
  readonly subjectControl = this.notificationForm.controls.subject;
  readonly messageControl = this.notificationForm.controls.message;
  readonly validFromControl = this.notificationForm.controls.validFrom;
  readonly validToControl = this.notificationForm.controls.validTo;
  readonly categoryControl = this.notificationForm.controls.category;

  readonly messageEditorModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      [
        { background: ['orange', '#f97316', '#f472b6', '#38bdf8', '#34d399'] },
        { color: ['red', '#ef4444', '#3b82f6', '#22c55e', '#f59e0b', '#6b7280'] },
      ],
    ],
  };

  constructor() {
    effect(() => {
      const required = this.showDateRange() ? [Validators.nullValidator] : [];
      this.validFromControl.setValidators(required);
      this.validToControl.setValidators(required);
      this.validFromControl.updateValueAndValidity({ emitEvent: false });
      this.validToControl.updateValueAndValidity({ emitEvent: false });
    });

    effect(() => {
      if (this.showCategories()) {
        this.categoryControl.setValidators([Validators.required]);
      } else {
        this.categoryControl.clearValidators();
      }
      this.categoryControl.updateValueAndValidity({ emitEvent: false });
    });

    effect(() => {
      if (!this.isOpen()) {
        this.notificationForm.reset({
          subject: '',
          message: '',
          validFrom: '',
          validTo: '',
          category: null,
        });
        this.notificationForm.markAsPristine();
        this.notificationForm.markAsUntouched();
      }
    });

    effect(() => {
      if (this.isLoading()) {
        this.notificationForm.disable({ emitEvent: false });
        return;
      }

      this.notificationForm.enable({ emitEvent: false });
    });
  }

  requestClose() {
    this.closeDialog.emit();
  }

  messageLength(): number {
    return extractRichTextPlainText(this.messageControl.value).length;
  }

  submitNotification() {
    if (this.isLoading()) {
      return;
    }

    if (this.notificationForm.invalid) {
      this.notificationForm.markAllAsTouched();
      return;
    }

    const { subject, message, validFrom, validTo, category } = this.notificationForm.getRawValue();
    this.submitForm.emit({
      subject,
      message,
      ...(this.showDateRange() && { validFrom, validTo }),
      ...(category !== null && { categoryId: category }),
    });
  }
}
